import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, setVip } from "@/lib/api";
import { useVip } from "@/hooks/useVip";
import { LogoMark } from "./Logo";
import { Reveal, SectionLabel } from "./Reveal";

const PERKS = [
  "$5 off every JayPanduh show for a full year",
  "10% off Jay-B Real & Panduh merch",
  "10% off official JayPanduh merch",
  "Your own member code to show at the door & checkout",
];

const PassCard = ({ code }) => (
  <motion.div
    initial={{ rotate: -6, y: 40, opacity: 0 }}
    whileInView={{ rotate: -3, y: 0, opacity: 1 }}
    whileHover={{ rotate: 0, scale: 1.02 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ type: "spring", stiffness: 90, damping: 14 }}
    data-testid="vip-pass-card"
    className="ticket-edge relative bg-[#56ED4A] text-black p-8 md:p-10 w-full max-w-md mx-auto lg:mx-0 shadow-[0_40px_80px_-20px_rgba(86,237,74,0.35)]"
  >
    <div className="flex justify-between items-start">
      <LogoMark className="w-16 h-16" />
      <span className="font-mono text-xs uppercase tracking-[0.25em]">Admit · VIP</span>
    </div>
    <p className="font-display uppercase text-5xl md:text-6xl leading-[0.85] mt-10">The JayPanduh Exclusive</p>
    <div className="mt-8 border-t-2 border-dashed border-black/40 pt-5 flex justify-between items-end font-mono text-xs uppercase tracking-[0.2em]">
      <div>
        <div className="opacity-70">Member code</div>
        <div data-testid="vip-pass-code" className="text-base font-semibold mt-1">{code || "JAYPANDUH-??????"}</div>
      </div>
      <div className="text-right"><div className="opacity-70">Good for 1 year</div><div className="text-base font-semibold mt-1">$9.99</div></div>
    </div>
  </motion.div>
);

const BuyForm = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const buy = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { email, name, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={buy} className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="vip-buy-form">
      <input data-testid="vip-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-transparent border border-white/25 px-4 py-4 focus:border-[#56ED4A] outline-none" />
      <input data-testid="vip-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for your pass" className="bg-transparent border border-white/25 px-4 py-4 focus:border-[#56ED4A] outline-none" />
      <button data-testid="vip-buy-button" disabled={busy} className="sm:col-span-2 bg-[#56ED4A] text-black py-5 font-mono uppercase tracking-[0.2em] text-sm font-semibold hover:bg-white transition-colors flex justify-center items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={18} /> : null} Get VIP access — $9.99
      </button>
    </form>
  );
};

const HaveCode = () => {
  const [code, setCode] = useState("");
  const check = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get(`/vip/verify/${encodeURIComponent(code)}`);
      if (data?.valid) { setVip(data.code); toast.success("Welcome back, VIP."); }
      else if (data?.code) toast.error("That membership has ended. Renew below with the same email to keep your code.");
      else toast.error("That code isn't valid.");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={check} className="mt-6 flex gap-2" data-testid="vip-restore-form">
      <input data-testid="vip-restore-input" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="Already a member? Enter code" className="flex-1 min-w-0 bg-transparent border-b border-white/25 px-1 py-3 font-mono text-sm uppercase focus:border-[#56ED4A] outline-none" />
      <button data-testid="vip-restore-btn" className="font-mono text-xs uppercase tracking-[0.2em] text-[#56ED4A] px-3">Apply</button>
    </form>
  );
};

export const VipPass = () => {
  const vip = useVip();
  return (
    <section id="vip" data-testid="vip-section" className="relative px-5 md:px-10 py-24 md:py-36 overflow-hidden">
      <div className="absolute -right-40 top-10 w-[600px] h-[600px] bg-[#56ED4A]/10 blur-[120px] pointer-events-none" />
      <div className="relative grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <SectionLabel index="06">VIP access</SectionLabel>
          <h2 className="font-display uppercase text-6xl md:text-8xl lg:text-9xl leading-[0.85] mt-6">Buy in <span className="text-[#56ED4A]">once.</span><br />Save every show.</h2>
          <ul className="mt-10 space-y-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-4 text-base md:text-lg text-zinc-200"><span className="w-7 h-7 grid place-items-center bg-[#56ED4A] text-black shrink-0"><Check size={16} /></span>{p}</li>
            ))}
          </ul>
          {vip ? (
            <p data-testid="vip-active-msg" className="mt-10 font-mono uppercase tracking-[0.2em] text-sm text-[#56ED4A]">You're in. Show code {vip} for $5 off shows + 10% off merch.</p>
          ) : (<><BuyForm /><HaveCode /></>)}
        </Reveal>
        <PassCard code={vip} />
      </div>
    </section>
  );
};
